/**
 * Traducciones del panel de administración: catálogo de productos,
 * catálogo de paquetes, sus formularios, y el armazón del panel.
 *
 * Este archivo es SUYO. Ver la nota de `shop.ts`.
 *
 * Las CLAVES son el texto en español, tal cual se escribió en el
 * componente. Si una clave no está aquí, la interfaz enseña la clave —o
 * sea, el español— y no un hueco: una traducción que falta se ve como
 * un idioma equivocado, nunca como una pantalla rota.
 */
export const adminEn: Record<string, string> = {
  /*
   * OJO con los duplicados: `resources.ts` funde common + shop + admin en
   * UN solo mapa, y este archivo se extiende AL FINAL — cualquier clave
   * repetida aquí GANA sobre la de la tienda. Por eso las que la tienda ya
   * traduce ("Catálogo", "Ordenar por", "Precio: de menor a mayor",
   * "Nombre", "Iniciar sesión"…) NO se repiten aquí: el panel las hereda,
   * y el día que la tienda cambie una redacción no se queda con dos.
   */
  // ── Armazón y acceso ────────────────────────────────────────────
  Productos: "Products",
  "Entra para continuar": "Sign in to continue",
  "No aplica": "Not required",
  "Fecha de compra": "Purchase date",
  "Abrir link de pago de Stripe": "Open Stripe payment link",
  "Vence el {{fecha}}": "Expires {{fecha}}",
  "Copiar link": "Copy link",
  "Copiado": "Copied",
  "El link de pago anterior ya venció.": "The previous payment link has expired.",
  "Esta orden no tiene un link de pago guardado.": "This order has no saved payment link.",
  "Generar link de pago": "Generate payment link",
  "Esta sección es del panel de administración.":
    "This section belongs to the admin panel.",
  "Esta sección es solo para super administradores":
    "This section is for super admins only",
  "Tu cuenta no tiene ese permiso. Si crees que debería tenerlo, pídelo a quien administra el sistema.":
    "Your account doesn't have that permission. If you think it should, ask whoever administers the system.",

  // ── Listado ─────────────────────────────────────────────────────
  "{{total}} productos en el catálogo.": "{{total}} products in the catalog.",
  "Nuevo producto": "New product",
  "Productos del catálogo": "Catalog products",
  Producto: "Product",
  Categoría: "Category",
  Precio: "Price",
  Existencias: "Stock",
  Estado: "Status",
  Acciones: "Actions",
  Archivar: "Archive",
  "Ya está archivado": "Already archived",
  "Sin control de existencias": "No stock tracking",
  Agotado: "Out of stock",
  "Quedan pocas": "Running low",

  // Filtros
  "Nombre o slug…": "Name or slug…",
  "Todos los estados": "All statuses",
  "Todas las categorías": "All categories",
  "Orden del catálogo": "Catalog order",
  "Limpiar filtros": "Clear filters",

  // Estados del producto
  Publicado: "Published",
  Borrador: "Draft",
  Archivado: "Archived",

  // Estados de la lista
  "No se pudo cargar el catálogo": "The catalog couldn't be loaded",
  "Sin resultados": "No results",
  "Ningún producto coincide con lo que buscas.":
    "No product matches what you're looking for.",
  "Todavía no hay productos": "No products yet",
  "Crea el primero para empezar el catálogo.":
    "Create the first one to start the catalog.",

  // Paginación
  Mostrando: "Showing",
  de: "of",
  Página: "Page",
  Anterior: "Previous",

  // Archivar
  "¿Archivar este producto?": "Archive this product?",
  "«{{name}}» dejará de verse en la tienda. No se borra: los pedidos que ya lo incluyen lo siguen encontrando.":
    "“{{name}}” will stop showing in the store. It isn't deleted: orders that already include it still find it.",
  "No se pudo archivar el producto": "The product couldn't be archived",

  // ── Formulario ──────────────────────────────────────────────────
  "Editar producto": "Edit product",
  "Los cambios se ven en la tienda en cuanto guardas.":
    "Changes show up in the store as soon as you save.",
  "Un producto nuevo nace como borrador: nadie lo ve hasta publicarlo.":
    "A new product starts as a draft: nobody sees it until you publish it.",
  "Guardar cambios": "Save changes",
  "Crear producto": "Create product",
  "No se pudo cargar el producto": "The product couldn't be loaded",

  // Contenido y traducciones
  Contenido: "Content",
  "Idioma del contenido": "Content language",
  Completo: "Complete",
  Incompleto: "Incomplete",
  "Sin traducir": "Not translated",
  "El inglés está incompleto: escribiste algo pero falta el nombre.":
    "English is incomplete: you wrote something but the name is missing.",
  "Colágeno hidrolizado": "Hydrolyzed collagen",
  Eslogan: "Tagline",
  "Una línea corta, debajo del nombre.": "One short line, under the name.",
  Descripción: "Description",
  "Qué es, para qué sirve y cómo se toma.":
    "What it is, what it's for, and how to take it.",

  // Catálogo
  "El slug no se cambia: rompería los enlaces ya compartidos.":
    "The slug can't be changed: it would break every link already shared.",
  "Se sugiere desde el nombre. Solo minúsculas, números y guiones.":
    "Suggested from the name. Lowercase, numbers and hyphens only.",
  Suplementos: "Supplements",
  Presentación: "Presentation",
  "Frasco de 60 cápsulas": "Bottle of 60 capsules",
  "Vacío = este producto no lleva control de inventario.":
    "Empty = this product doesn't track inventory.",
  Orden: "Sort order",
  "El número más bajo aparece primero.": "The lowest number comes first.",

  // Precios
  Precios: "Prices",
  "Precio tachado": "Compare-at price",
  "Opcional. Debe ser mayor que el precio.":
    "Optional. It must be higher than the price.",
  "Escribe un monto válido, por ejemplo 1299.50":
    "Write a valid amount, for example 1299.50",
  "El precio tachado debe ser mayor que el precio":
    "The compare-at price must be higher than the price",
  "Pon al menos un precio": "Set at least one price",
  "Las existencias son un número entero, 0 o más":
    "Stock is a whole number, 0 or more",
  "El orden es un número entero": "Sort order is a whole number",

  // Imagen
  Imagen: "Image",
  "Pega una dirección completa (https://…) o una ruta de este sitio (/assets/…).":
    "Paste a full address (https://…) or a path on this site (/assets/…).",
  "Dirección de la imagen": "Image address",
  "Vista previa": "Preview",

  // Errores del API
  "Tu cuenta no tiene permiso para esta acción.":
    "Your account doesn't have permission for this action.",
  "Tu sesión expiró. Vuelve a entrar.": "Your session expired. Sign in again.",
  "Ese producto ya no existe.": "That product no longer exists.",
  "Ya existe un producto con ese slug.": "A product with that slug already exists.",
  "Algo salió mal. Vuelve a intentarlo.": "Something went wrong. Try again.",

  // ═══════════════════════════════════════════════════════════════
  // El formulario, ahora un wizard de diez pasos
  // ═══════════════════════════════════════════════════════════════

  // Recorrido
  "Progreso del producto": "Product progress",
  "Puedes guardar sin llegar al final: lo que falte se completa después.":
    "You can save without reaching the end: whatever is missing can wait.",

  // Rótulos y frases de los pasos
  Información: "Information",
  "Cómo se llama el producto y qué promete, en los dos idiomas.":
    "What the product is called and what it promises, in both languages.",
  Clasificación: "Classification",
  "Dónde vive en el catálogo y si se puede ver.":
    "Where it lives in the catalog and whether it can be seen.",
  "Una fila por moneda. Se escriben en pesos; se guardan en centavos.":
    "One row per currency. Written in pesos; stored in cents.",
  Puntos: "Points",
  "Lo que este producto aporta al plan de compensación.":
    "What this product contributes to the compensation plan.",
  "La lista de promesas, en el orden en que se leen.":
    "The list of promises, in the order they are read.",
  Media: "Media",
  "Las fotos y la ficha técnica.": "The photos and the datasheet.",
  Envío: "Shipping",
  "Si se manda a algún lado, cuánto pesa y cuánto ocupa.":
    "Whether it ships anywhere, how much it weighs and how much room it takes.",
  Vigencia: "Validity",
  "Desde cuándo se vende, hasta cuándo, y cuánto dura lo que se compra.":
    "From when it sells, until when, and how long what is bought lasts.",
  Negocio: "Business",
  "Paquete mayorista, franquiciatarios y la letra de la etiqueta.":
    "Wholesale package, franchisees and the fine print on the label.",
  Editorial: "Editorial",
  "Los once bloques de la ficha larga. Todos opcionales.":
    "The eleven blocks of the long page. All optional.",

  // Paso 1 · Información
  "Dirección en la tienda": "Address in the store",
  "La parte legible de la URL. Se elige una vez y no se cambia.":
    "The readable part of the URL. Chosen once and never changed.",

  // Paso 2 · Clasificación
  "Qué es este producto y dónde aparece.":
    "What this product is and where it shows up.",
  "Tipo de producto": "Product type",
  "Sin clasificar": "Unclassified",
  "Tipo de paquete": "Package type",
  "No es un paquete": "Not a package",
  "Solo para los paquetes: inicial, membresía o recompra.":
    "Packages only: starter, membership or repurchase.",
  "Se puede comprar ahora": "Can be bought right now",
  "Desmárcalo para dejarlo publicado pero sin venta.":
    "Uncheck it to keep it published but not for sale.",
  Suplemento: "Supplement",
  Membresía: "Membership",
  "Paquete de negocio": "Business package",
  Kit: "Kit",
  Servicio: "Service",
  Otro: "Other",
  "Paquete inicial": "Starter package",
  Recompra: "Repurchase",

  // Paso 3 · Precios
  "Una fila por moneda. Escríbelos en pesos, como se leen: 1299.50. Hace falta al menos uno.":
    "One row per currency. Write them in pesos, the way they read: 1299.50. At least one is required.",
  Moneda: "Currency",
  "Código ISO de tres letras.": "Three-letter ISO code.",
  "Añadir moneda": "Add currency",
  "Quitar la moneda {{code}}": "Remove the {{code}} currency",
  "Quitar la moneda {{n}}": "Remove currency {{n}}",
  "Escribe el código de tres letras para poder poner los importes.":
    "Write the three-letter code to be able to set the amounts.",
  "Usa un código de tres letras: MXN": "Use a three-letter code: MXN",
  "Esa moneda ya está en la lista": "That currency is already on the list",
  "Precio público": "Public price",
  "Precio de socio": "Member price",
  "Lo que paga quien tiene membresía.": "What a member pays.",
  "Precio en promoción": "Promo price",
  Descuento: "Discount",
  "El precio rebajado se calcula desde aquí, para que no haya dos versiones.":
    "The discounted price is derived from here, so there are never two versions.",
  "El descuento está activo": "The discount is active",
  Tipo: "Type",
  Porcentaje: "Percentage",
  "Monto fijo": "Fixed amount",
  "Cuánto se descuenta": "How much comes off",
  "En dinero, como se lee: 150.00": "In money, the way it reads: 150.00",
  "En porcentaje, de 0 a 100.": "As a percentage, from 0 to 100.",
  "Escribe cuánto se descuenta": "Write how much comes off",
  "El porcentaje va de 0 a 100": "The percentage goes from 0 to 100",

  // Paso 4 · Puntos
  "Cuánto suma este producto. Enteros: los puntos no se parten.":
    "How much this product adds. Whole numbers: points don't split.",
  "VP · puntos de volumen": "VP · volume points",
  "Es el número que el carrito compara contra el mínimo de un paquete.":
    "It's the number the cart compares against a package's minimum.",
  "CV · volumen comisionable": "CV · commissionable volume",
  "Puede ser distinto del VP.": "It can differ from the VP.",
  "VP en promoción": "Promo VP",
  "CV en promoción": "Promo CV",
  "Qué hace este producto por quien lo compra":
    "What this product does for whoever buys it",
  "Cada casilla es una regla del plan. Ninguna es obligatoria.":
    "Each checkbox is a rule of the plan. None is required.",
  "Cuenta para calificar el periodo": "Counts toward qualifying for the period",
  "Suma al volumen personal de quien compra":
    "Adds to the buyer's personal volume",
  "Suma al volumen de grupo de su línea ascendente":
    "Adds to the group volume of their upline",
  "Cuenta para subir de rango": "Counts toward moving up a rank",
  "Es un producto de recompra": "It's a repurchase product",
  "Comprarlo mantiene la cuenta activa": "Buying it keeps the account active",
  "Para qué califica": "What it qualifies for",
  "Texto libre, tal como se le explica a un socio.":
    "Free text, the way it's explained to a member.",
  "Beneficios de membresía": "Membership benefits",
  "Escribe un número entero": "Write a whole number",
  "Escribe un número, 0 o más": "Write a number, 0 or more",

  // Paso 5 · Beneficios
  "Lo que promete el producto, una promesa por línea.":
    "What the product promises, one promise per line.",
  "Caben hasta 12.": "Up to 12 fit.",
  "Añadir beneficio": "Add benefit",
  Beneficio: "Benefit",
  "Beneficio {{n}}": "Benefit {{n}}",
  "Todavía no hay beneficios. Añade el primero.":
    "No benefits yet. Add the first one.",
  "Mejora la elasticidad de la piel": "Improves skin elasticity",

  // Listas dinámicas, en general
  "Subir {{item}}": "Move {{item}} up",
  "Bajar {{item}}": "Move {{item}} down",
  "Quitar {{item}}": "Remove {{item}}",
  "Llegaste al máximo de {{max}}.": "You reached the maximum of {{max}}.",

  // Paso 6 · Media
  Galería: "Gallery",
  "Las demás fotos de la ficha, en el orden en que se pasan.":
    "The rest of the photos, in the order they're flipped through.",
  "Añadir foto": "Add photo",
  "Foto {{n}}": "Photo {{n}}",
  "Sin galería, la ficha enseña solo la foto principal.":
    "With no gallery, the page shows only the main photo.",
  "Ficha técnica": "Datasheet",
  "El PDF que se puede descargar desde la ficha.":
    "The PDF that can be downloaded from the page.",
  "Dirección del archivo": "File address",
  "Nombre visible": "Visible name",
  "Ficha técnica (PDF)": "Datasheet (PDF)",
  "Lo que se lee en el enlace de descarga.":
    "What the download link reads.",

  // Paso 7 · Envío
  Inventario: "Inventory",
  "Cuántas unidades quedan, si es que se llevan las cuentas.":
    "How many units are left, if anyone is counting.",
  "Si hay que mandarlo, cuánto pesa y cuánto ocupa.":
    "If it has to be shipped, how much it weighs and how much room it takes.",
  "Este producto se envía": "This product ships",
  "Apágalo para lo que no viaja: membresías, servicios, cursos.":
    "Turn it off for what doesn't travel: memberships, services, courses.",
  "Peso (gramos)": "Weight (grams)",
  "Largo (cm)": "Length (cm)",
  "Ancho (cm)": "Width (cm)",
  "Alto (cm)": "Height (cm)",

  // Paso 8 · Vigencia
  "Ventana de venta": "Sales window",
  "Entre qué fechas se puede comprar. Vacío = siempre.":
    "Between which dates it can be bought. Empty = always.",
  Desde: "From",
  Hasta: "Until",
  "Duración de lo que se compra": "How long what is bought lasts",
  "Para membresías y programas: cuántos días valen.":
    "For memberships and programs: how many days they're worth.",
  "Días de duración": "Days it lasts",
  "Vacío = no caduca.": "Empty = it doesn't expire.",
  "Esa fecha no es válida": "That date isn't valid",
  "El fin no puede ir antes del inicio": "The end can't come before the start",

  // Paso 9 · Negocio
  "Solo para lo que se le vende a quien emprende, no al público.":
    "Only for what's sold to someone starting a business, not to the public.",
  "Es un paquete mayorista": "It's a wholesale package",
  "No aparece entre los paquetes personales.":
    "It doesn't show among personal packages.",
  "Descuento para franquiciatarios (%)": "Franchisee discount (%)",
  "De 0 a 50. Es el porcentaje extra para franquiciatarios activos.":
    "From 0 to 50. It's the extra percentage for active franchisees.",
  "El porcentaje va de 0 a 50": "The percentage goes from 0 to 50",
  "Lo que dice la etiqueta": "What the label says",
  "Copiado del bote, tal cual está impreso.":
    "Copied from the bottle, exactly as printed.",
  Posología: "Dosage",
  "Tomar 2 cápsulas al día con alimentos":
    "Take 2 capsules a day with food",
  "Aviso legal": "Legal notice",
  "No es medicamento, alérgenos, avisos sanitarios.":
    "Not a medicine, allergens, health warnings.",

  // Paso 10 · Editorial
  "Ficha editorial": "Editorial page",
  "Los once bloques de la ficha larga. Escribe los que tengas: ninguno es obligatorio y se pueden dejar para después.":
    "The eleven blocks of the long page. Write the ones you have: none is required and they can wait.",
  "La ficha en inglés cae al español BLOQUE a bloque: lo que no escribas aquí se lee en español, entero.":
    "The English page falls back to Spanish BLOCK by block: whatever you don't write here reads in Spanish, whole.",
  Escrito: "Written",
  Vacío: "Empty",
  "Con un problema": "Has a problem",
  Icono: "Icon",
  "Sin icono": "No icon",
  Tema: "Theme",
  "El de la ficha": "The page's own",
  Claro: "Light",
  Oscuro: "Dark",
  Acento: "Accent",
  "Título del bloque": "Block title",
  Entradilla: "Lead-in",
  "Ponle título al bloque: sin él no se puede guardar":
    "Give the block a title: it can't be saved without one",

  // Los doce iconos
  Rayo: "Bolt",
  ADN: "DNA",
  Escudo: "Shield",
  Hoja: "Leaf",
  Corazón: "Heart",
  Cerebro: "Brain",
  Matraz: "Flask",
  Destellos: "Sparkles",
  Reloj: "Clock",
  Gota: "Droplet",
  Sol: "Sun",
  Pulso: "Pulse",

  // Bloques editoriales
  "Etiqueta del héroe": "Hero badge",
  "La palabra que va sobre la foto grande. Máximo 40 caracteres.":
    "The word that sits over the big photo. 40 characters max.",
  Etiqueta: "Badge",
  "Más vendido": "Best seller",
  "Precio tachado de la ficha": "Compare-at price on the page",
  "Lo que se enseña tachado en la ficha larga, moneda por moneda.":
    "What's shown struck through on the long page, currency by currency.",
  "Añade una moneda en el paso de precios para poder escribir aquí.":
    "Add a currency in the pricing step to be able to write here.",
  "Datos de un vistazo": "At a glance",
  "Los cuatro datos que van bajo la compra. Corto: caben 60 letras.":
    "The four facts under the buy button. Short: 60 letters fit.",
  "60 cápsulas": "60 capsules",
  Dosis: "Dose",
  "2 al día": "2 a day",
  Duración: "Duration",
  "30 días": "30 days",
  Certificación: "Certification",
  Cofepris: "Cofepris",
  Pilares: "Pillars",
  "De tres a cuatro razones, cada una con su icono.":
    "Three or four reasons, each with its icon.",
  "Por qué funciona": "Why it works",
  "Opcional. Un párrafo antes de los pilares.":
    "Optional. A paragraph before the pillars.",
  "Añadir pilar": "Add pillar",
  "Pilar {{n}}": "Pillar {{n}}",
  "Todavía no hay pilares. Añade el primero.": "No pillars yet. Add the first one.",
  "Título del pilar": "Pillar title",
  "Absorción real": "Real absorption",
  Ingredientes: "Ingredients",
  "Qué lleva dentro y para qué sirve cada cosa.":
    "What's inside and what each thing is for.",
  "Qué lleva dentro": "What's inside",
  "Cómo se pinta el bloque: sobre claro o sobre oscuro.":
    "How the block is painted: on light or on dark.",
  "Añadir ingrediente": "Add ingredient",
  "Ingrediente {{n}}": "Ingredient {{n}}",
  "Todavía no hay ingredientes. Añade el primero.":
    "No ingredients yet. Add the first one.",
  "Nombre del ingrediente": "Ingredient name",
  "Renglón corto": "Short line",
  "Coenzima de la energía celular": "Coenzyme of cellular energy",
  "Escríbelo normal: las mayúsculas las pone la ficha.":
    "Write it normally: the page does the uppercase.",
  "Tabla nutrimental": "Nutrition table",
  "Una fila por ingrediente, con su cantidad y su función.":
    "One row per ingredient, with its amount and its function.",
  "Información nutrimental": "Nutrition facts",
  "Tipo de tabla": "Table type",
  "Análisis por porción": "Per-serving analysis",
  "Nota al pie": "Footnote",
  "El texto pequeño bajo la tabla.": "The small print under the table.",
  "Añadir fila": "Add row",
  "Fila {{n}}": "Row {{n}}",
  "Todavía no hay filas. Añade la primera.": "No rows yet. Add the first one.",
  Ingrediente: "Ingredient",
  Cantidad: "Amount",
  "Texto libre: cabe «c.s.p.».": "Free text: “q.s.” fits.",
  Unidad: "Unit",
  Función: "Function",
  Proceso: "Process",
  "Los pasos, en orden. La ficha los numera sola.":
    "The steps, in order. The page numbers them itself.",
  "Cómo se hace": "How it's made",
  "Añadir paso": "Add step",
  "Paso {{n}}": "Step {{n}}",
  "Todavía no hay pasos. Añade el primero.": "No steps yet. Add the first one.",
  "Nombre del paso": "Step name",
  "Extracción en frío": "Cold extraction",
  Cifras: "Figures",
  "Los números grandes que se leen de lejos.":
    "The big numbers you read from across the room.",
  "En números": "By the numbers",
  "Añadir cifra": "Add figure",
  Cifra: "Figure",
  "Cifra {{n}}": "Figure {{n}}",
  "Todavía no hay cifras. Añade la primera.": "No figures yet. Add the first one.",
  "Con su símbolo: «3×», «24 h».": "With its symbol: “3×”, “24 h”.",
  "de absorción": "absorption",
  Detalle: "Detail",
  Comparación: "Comparison",
  "Dos lados enfrentados: antes y después, con y sin.":
    "Two sides facing off: before and after, with and without.",
  "La diferencia": "The difference",
  "Lado izquierdo": "Left side",
  "Lado derecho": "Right side",
  Título: "Title",
  "Sin el producto": "Without the product",
  "Con el producto": "With the product",
  "Nota destacada": "Callout",
  "Un recuadro con algo que hay que leer sí o sí.":
    "A box with something that has to be read.",
  "Título de la nota": "Callout title",
  "Sin tipo": "No type",
  Ciencia: "Science",
  Advertencia: "Warning",
  Contexto: "Context",
  Cita: "Quote",
  "Qué dice la ciencia": "What the science says",
  Texto: "Text",
  Cierre: "Closing",
  "La frase final de la ficha.": "The closing line of the page.",
  Frase: "Line",

  // ═══════════════════════════════════════════════════════════════
  // PAQUETES · listado y formulario
  //
  // Lo que NO está aquí, por la regla de arriba: todo lo que la
  // tienda, el panel de productos o la rejilla pública ya traducen
  // —"Categoría", "Estado", "Precio", "Descuento", "Orden", "Nombre",
  // "Recompra personal", "Negocio digital", "Limpiar filtros"…—. El
  // panel de paquetes las hereda tal cual, y el día que se cambie una
  // redacción no queda una versión distinta escondida aquí.
  //
  // Ojo con `Paquetes`: la tienda lo traduce como "Bundles" y esta
  // pantalla lo hereda, mientras que la rejilla pública habla de
  // "package". Es una discrepancia de la tienda, no de aquí; se
  // arregla en `shop.ts` y se arregla para todos a la vez.
  // ═══════════════════════════════════════════════════════════════

  // ── Listado ─────────────────────────────────────────────────────
  "{{total}} paquetes en el catálogo.": "{{total}} packages in the catalog.",
  "Nuevo paquete": "New package",
  "Paquetes del catálogo": "Catalog packages",
  Paquete: "Package",
  "{{n}} unidad": "{{n}} unit",
  "{{n}} unidades": "{{n}} units",
  "Mayor descuento": "Biggest discount",
  "hasta {{max}} %": "up to {{max}}%",
  "No se pudieron cargar los paquetes": "The packages couldn't be loaded",
  "Cargando los paquetes…": "Loading packages…",
  "Ningún paquete coincide con esos filtros.": "No package matches those filters.",
  "Todavía no hay paquetes": "No packages yet",
  "Un paquete junta varios productos y se cobra cada mes con descuento.":
    "A package bundles several products and is charged every month at a discount.",

  // ── Archivar ────────────────────────────────────────────────────
  "¿Archivar este paquete?": "Archive this package?",
  "«{{name}}» dejará de ofrecerse. No se borra, y las suscripciones que ya lo tienen contratado se siguen cobrando.":
    "“{{name}}” will stop being offered. It isn't deleted, and subscriptions that already have it keep being charged.",
  "No se pudo archivar el paquete": "The package couldn't be archived",

  // ── Errores del API ─────────────────────────────────────────────
  "Ese paquete ya no existe.": "That package no longer exists.",
  "Ya existe un paquete con ese slug.": "A package with that slug already exists.",
  "No se pudo cargar el paquete": "The package couldn't be loaded",

  // ── Formulario ──────────────────────────────────────────────────
  "Editar paquete": "Edit package",
  "Crear paquete": "Create package",
  "Un paquete nuevo nace como borrador: nadie lo ve hasta publicarlo.":
    "A new package starts as a draft: nobody sees it until you publish it.",
  "Se guarda el descuento, no el precio: el precio lo calcula el servidor.":
    "What's saved is the discount, not the price: the server computes the price.",
  "Paquete Bienestar": "Wellness Package",
  "Qué trae el paquete y para quién es.":
    "What the package includes and who it's for.",
  "Pega una dirección completa (https://…) de una imagen ya publicada.":
    "Paste a full address (https://…) of an image that's already published.",

  // ── Los productos del paquete ───────────────────────────────────
  "Productos del paquete": "Package products",
  "Elige qué lleva y cuántos de cada uno. De aquí salen el precio y los VP: el paquete no tiene precio propio.":
    "Pick what it includes and how many of each. The price and the VP come from here: a package has no price of its own.",
  "Añadir producto": "Add product",
  "Producto {{n}}": "Product {{n}}",
  "Todavía no hay productos. Añade el primero.":
    "No products yet. Add the first one.",
  "Elige un producto": "Pick a product",
  "Cargando el catálogo…": "Loading the catalog…",
  Cuántos: "How many",
  "{{points}} VP": "{{points}} VP",
  "Sin VP capturados": "No VP captured",
  "Este producto ya no está en el catálogo: quítalo o elige otro.":
    "This product is no longer in the catalog: remove it or pick another one.",

  // ── Categoría y descuento ───────────────────────────────────────
  "Categoría y descuento": "Category and discount",
  "La categoría decide el techo del descuento. El precio no se escribe: se calcula quitándole este porcentaje a la suma de los productos.":
    "The category sets the discount ceiling. The price isn't typed: it's computed by taking this percentage off the sum of the products.",
  "Los de negocio digital no aparecen entre los paquetes personales.":
    "Digital business ones don't show among personal packages.",
  "Descuento (%)": "Discount (%)",
  "Entre {{min}} % y {{max}} % para esta categoría.":
    "Between {{min}}% and {{max}}% for this category.",
  "Un porcentaje entero.": "A whole percentage.",
  "Ese descuento no cabe en esta categoría":
    "That discount doesn't fit this category",
  "«{{category}}» admite hasta {{max}} % y tienes {{percent}} %. Bájalo, o cambia la categoría.":
    "“{{category}}” allows up to {{max}}% and you have {{percent}}%. Lower it, or change the category.",
  "El descuento mínimo es {{min}} % y tienes {{percent}} %. Por debajo de ahí, el paquete no convence a nadie.":
    "The minimum discount is {{min}}% and you have {{percent}}%. Below that, the package convinces nobody.",
  "Extra para franquiciatarios (%)": "Franchisee extra (%)",
  "De 0 a 50. Es el porcentaje adicional para franquiciatarios activos; no entra en el precio de arriba.":
    "From 0 to 50. It's the extra percentage for active franchisees; it isn't part of the price above.",

  // ── El precio calculado ─────────────────────────────────────────
  "Lo que va a costar": "What it will cost",
  "No se teclea: sale de los productos del paquete y del descuento. El servidor hace esta misma cuenta al guardar.":
    "Not typed: it comes from the package's products and the discount. The server runs this same math when you save.",
  Calculado: "Calculated",
  "Calculando el precio…": "Calculating the price…",
  "No se pudo cargar el catálogo de productos":
    "The product catalog couldn't be loaded",
  "Sin él no se puede calcular el precio. Recarga la página.":
    "Without it the price can't be calculated. Reload the page.",
  "El catálogo tiene más productos de los que caben en el selector: si un paquete usa uno de los que faltan, su precio aquí sale incompleto.":
    "The catalog has more products than fit in the picker: if a package uses one of the missing ones, its price here comes out incomplete.",
  "Falta un producto del paquete": "A package product is missing",
  "Hay {{count}} producto(s) que el catálogo no encuentra. Hasta que se arreglen, esta cuenta está incompleta.":
    "There are {{count}} product(s) the catalog can't find. Until they're fixed, this total is incomplete.",
  "Una moneda se quedó fuera": "A currency was left out",
  "En {{currencies}} no todos los productos tienen precio, así que el paquete no se puede cobrar en esa moneda. Ponles precio o quítalos del paquete.":
    "In {{currencies}} not every product has a price, so the package can't be charged in that currency. Price them, or take them out of the package.",
  "Elige los productos del paquete y aquí aparece lo que va a cobrar.":
    "Pick the package's products and what it will charge shows up here.",
  "Ninguno de los productos elegidos tiene precio todavía, así que no hay nada que cobrar.":
    "None of the chosen products has a price yet, so there's nothing to charge.",
  "Se cobra en {{currency}}": "Charged in {{currency}}",
  Suelto: "Separately",
  Ahorro: "Savings",
  "VP del paquete": "Package VP",
  "La suma de los VP de sus productos, por su cantidad.":
    "The sum of its products' VP, times their quantity.",
  "Hay productos sin VP capturados: este total se queda corto.":
    "Some products have no VP captured: this total falls short.",
  "Descuento aplicado": "Discount applied",
  "Escribe un descuento entero para ver el precio final.":
    "Write a whole discount to see the final price.",
  "{{units}} unidades en {{lines}} productos.":
    "{{units}} units across {{lines}} products.",
};
